import { forwardRef, useCallback } from "react";
import {
  MEETING_JOURNAL_LAYOUT,
  type JournalAiMode,
  type MeetingPrescriptionOrder,
  type UpdateJournalSections,
  type UpdateMeetingJournal,
} from "@care-by-ella/contracts";
import {
  JournalSectionsCard,
  type JournalSectionsHandle,
} from "@/components/JournalSectionsCard";
import {
  fetchMeetingJournal,
  generateJournalSectionAi,
  saveMeetingJournal,
} from "@/lib/clinician-api";
import { journalSectionAiRequest } from "@/lib/journal-ai-request";

// TES-904 steps 1–3 — the care meeting's journal note: the shared seven-box
// card (JournalSectionsCard), read from and saved to the meeting.
//
// TES-1019 (Emelie, points 3–6) — the doctor writes straight into the right
// box: no global "Stödord" box and no AI "Skriv ut" that routed it across
// the headings. TES-1328 — "Åtgärd och behandlingsplan" is locked, showing
// only its hint; its text is generated from the Recept, the chosen
// kompletteringar and the monthly follow-up when the note is compiled, and
// there is no "Ordination" heading. There is no "Sammanställ journal" here:
// the finished note is assembled, copied and confirmed in the Webdoc step
// after "Sammanställ journalanteckning" (MeetingWorkspace, TES-1294).

export type { JournalValues } from "@/components/JournalSectionsCard";
export type MeetingJournalHandle = JournalSectionsHandle;

export const MeetingJournalCard = forwardRef<
  MeetingJournalHandle,
  {
    patientId: string;
    meetingId: string;
    onUnauthorized: () => void;
    // TES-956 — the order entered in the meeting's Recept fields, as it
    // stands on screen (the card itself no longer shows it, TES-1328).
    order?: MeetingPrescriptionOrder | null;
    // TES-1019 — whether the AI switch is on in this environment (the forms
    // overview reports it). Off or unknown: no AI help under the boxes.
    aiAvailable?: boolean | null;
    // TES-1312 — "Sammanställ ändå" on the card's raw-headings notice.
    onCompileAnyway?: () => void;
  }
>(function MeetingJournalCard(
  { patientId, meetingId, onUnauthorized, order, aiAvailable, onCompileAnyway },
  ref,
) {
  const sectionAi = useCallback(
    (key: string, mode: JournalAiMode, text: string) =>
      generateJournalSectionAi(
        patientId,
        meetingId,
        journalSectionAiRequest(key, mode, text),
      ),
    [patientId, meetingId],
  );
  const load = useCallback(
    () => fetchMeetingJournal(patientId, meetingId),
    [patientId, meetingId],
  );
  const save = useCallback(
    // The card only sends keys of the layout it was given (the meeting's), and
    // the API validates them again.
    (input: UpdateJournalSections) =>
      saveMeetingJournal(patientId, meetingId, input as UpdateMeetingJournal),
    [patientId, meetingId],
  );
  return (
    <JournalSectionsCard
      ref={ref}
      idPrefix={meetingId}
      layout={MEETING_JOURNAL_LAYOUT}
      load={load}
      save={save}
      onUnauthorized={onUnauthorized}
      order={order}
      sectionAi={aiAvailable ? sectionAi : undefined}
      onCompileAnyway={onCompileAnyway}
    />
  );
});
